import { clamp } from "@utils/utils";

export type AdsrRamp = "linear" | "exponential";

export interface AdsrEnvelope {
  maxTime: number;
  attack: { t: number; v: number; ramp: AdsrRamp };
  decay: { t: number; v: number; ramp: AdsrRamp };
  sustain: { t: number; v?: number; ramp?: AdsrRamp };
  release: { t: number; ramp: AdsrRamp };
}

export type AdsrStage = "attack" | "decay" | "sustain" | "release";

export const AdsrStages: readonly AdsrStage[] = ["attack", "decay", "sustain", "release"];

export const AdsrEnvelopeDefault: AdsrEnvelope = {
  maxTime: 4,
  attack: { t: 0.3, v: 1, ramp: "exponential" },
  decay: { t: 0.8, v: 0.6, ramp: "exponential" },
  sustain: { t: 2.5 },
  release: { t: 3.5, ramp: "exponential" },
};

export interface AdsrPoint {
  stage: AdsrStage;
  t: number;
  v: number;
}

export function AdsrPoints(env: AdsrEnvelope): AdsrPoint[] {
  const sustainV = env.sustain.v ?? env.decay.v;
  return [
    { stage: "attack", t: env.attack.t, v: env.attack.v },
    { stage: "decay", t: env.decay.t, v: env.decay.v },
    { stage: "sustain", t: env.sustain.t, v: sustainV },
    { stage: "release", t: env.release.t, v: 0 },
  ];
}

const ExpCurve = 4;

function RampShape(u: number, ramp: AdsrRamp | undefined, rising: boolean): number {
  if (ramp !== "exponential") return u;
  const norm = Math.exp(ExpCurve) - 1;
  return rising
    ? (Math.exp(ExpCurve * u) - 1) / norm
    : 1 - (Math.exp(ExpCurve * (1 - u)) - 1) / norm;
}

export function AdsrCurve(env: AdsrEnvelope, perSegment = 24): { t: number; v: number }[] {
  const out = [{ t: 0, v: 0 }];
  let prev = { t: 0, v: 0 };
  for (const point of AdsrPoints(env)) {
    const ramp = env[point.stage].ramp;
    const rising = point.v > prev.v;
    for (let i = 1; i <= perSegment; i++) {
      const u = i / perSegment;
      out.push({
        t: prev.t + (point.t - prev.t) * u,
        v: prev.v + (point.v - prev.v) * RampShape(u, ramp, rising),
      });
    }
    prev = point;
  }
  return out;
}

export function AdsrMovePoint(
  env: AdsrEnvelope,
  stage: AdsrStage,
  t: number,
  v: number,
  minGap = 0.001,
): AdsrEnvelope {
  const next = { ...env };
  const level = clamp(v, 0, 1);
  switch (stage) {
    case "attack":
      next.attack = { ...env.attack, t: clamp(t, minGap, env.decay.t - minGap), v: level };
      break;
    case "decay":
      next.decay = {
        ...env.decay,
        t: clamp(t, env.attack.t + minGap, env.sustain.t - minGap),
        v: level,
      };
      break;
    case "sustain": {
      const st = clamp(t, env.decay.t + minGap, env.release.t - minGap);
      if (env.sustain.v === undefined) {
        next.sustain = { t: st };
        next.decay = { ...env.decay, v: level };
      } else {
        next.sustain = { ...env.sustain, t: st, v: level };
      }
      break;
    }
    case "release":
      next.release = { ...env.release, t: clamp(t, env.sustain.t + minGap, env.maxTime) };
      break;
  }
  return next;
}
